import { AbstractMesh, Axis, Space, Vector3 } from '@babylonjs/core';
import { Level } from '../Level';
import { ObstacleDistributionConfig } from '../ObstacleGenerator';
import { ObstacleBehavior } from './ObstacleBehavior';

export class ObstacleRocketBehavior extends ObstacleBehavior {
  private rushing: boolean = false;
  private currentPathIndex: number;
  private lateralOffset: number;
  private time: number = 0;

  constructor(startPosition: Vector3, pathIndex: number, config: ObstacleDistributionConfig, lateralOffset: number = 0) {
    super('obstacleRush', startPosition, pathIndex, config);
    this.currentPathIndex = pathIndex;
    this.lateralOffset = lateralOffset;
  }

  attach(mesh: AbstractMesh): void {
    super.attach(mesh);
  }

  update(deltaTime: number): void {
    if (!this.level || !this.level.player) return;

    this.time += deltaTime;

    if (!this.rushing) {
      const dist = Vector3.Distance(this.mesh.position, this.level.player.getPosition());
      if (dist < 80) {
        // Trigger range increased slightly
        this.rushing = true;
      }
    } else {
      // Rush towards player along path
      const moveSpeed = this.config.speed; // Adjust as needed
      this.currentPathIndex -= moveSpeed * deltaTime * 0.5; // 0.5 factor to adjust for index density

      if (this.currentPathIndex < Math.max(0, this.pathIndex - 16)) {
        this.currentPathIndex = 0;
        this.dispose(); // Reached start of path
        return;
      }

      // Always Calculate position at currentPathIndex
      const idx = Math.floor(this.currentPathIndex);
      const nextIdx = Math.min(idx + 1, this.level.pathPoints.length - 1);
      const t = this.currentPathIndex - idx;

      const p1 = this.level.pathPoints[idx];
      const p2 = this.level.pathPoints[nextIdx];

      // Interpolated position on path center
      const currentPos = Vector3.Lerp(p1, p2, t);

      // Tangent direction for the obstacle facing (tangent backwards)
      const tangent = p1.subtract(p2).normalize();

      // Forward tangent for lateral calc
      const forwardTangent = p2.subtract(p1).normalize();
      const lateral = new Vector3(-forwardTangent.z, 0, forwardTangent.x);

      // Apply offsets
      const finalPos = currentPos.subtract(lateral.scale(this.lateralOffset));

      finalPos.y += this.config.yOffset;

      if (this.config.lurch) {
        const lurchFrequency = 2;
        const lurchAmount = this.config.lurch * Math.sin(this.time * lurchFrequency);
        finalPos.addInPlace(lateral.scale(lurchAmount));
      }

      this.mesh.position = finalPos;

      if (!this.config.rotateY) {
        // face movement direction
        this.mesh.lookAt(this.mesh.position.add(tangent));
        // apply configured rotation
        if (this.config.rotation?.y !== 0) {
          this.mesh.rotate(Axis.Y, this.config.rotation.y, Space.LOCAL);
        }
      } else {
        // apply configured rotation
        this.mesh.rotate(Axis.Y, this.config.rotateY, Space.LOCAL);
      }
    }
  }

  initLevel(level: Level): ObstacleRocketBehavior {
    super.initLevel(level);
    this.level.game.sound.spatial('obstacleengine', this.mesh).then((sound) => {
      if (sound) {
        sound.loop = true;
        sound.play();
      }
    });
    return this;
  }

  clone(): ObstacleRocketBehavior {
    return new ObstacleRocketBehavior(this.startPosition, this.pathIndex, this.config, this.lateralOffset);
  }

  dispose() {
    if (this.mesh) {
      this.mesh.dispose();
    }
  }
}
